import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * A gallery of before/after images that open in a lightbox.
 *
 * Extends `DialogComponent` rather than nesting one. Refs bind to the nearest
 * `Component` ancestor (see `#isDescendant` in component.js), so a nested
 * `<dialog-component>` would own the `<dialog>` ref and put it out of reach here.
 * Subclassing keeps the triggers, the dialog and the large image in a single ref
 * scope, and inherits Escape, click-outside-to-close, scroll lock and animations.
 *
 * @typedef {object} GalleryRefs
 * @property {HTMLDialogElement} dialog - The lightbox dialog.
 * @property {HTMLElement} grid - The image grid.
 * @property {HTMLImageElement} lightboxImage - The large image inside the dialog.
 * @property {HTMLButtonElement[]} [items] - The clickable grid images.
 * @property {HTMLButtonElement} [previousButton] - Previous control.
 * @property {HTMLButtonElement} [nextButton] - Next control.
 *
 * @extends DialogComponent
 */
class TransformationGallery extends DialogComponent {
  requiredRefs = ['dialog', 'grid', 'lightboxImage'];

  /** @type {number} */
  #index = 0;

  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  connectedCallback() {
    super.connectedCallback();

    // Focus return is wired through the close event rather than by overriding
    // closeDialog, which the parent defines as an instance arrow-function property.
    this.addEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.addEventListener('keydown', this.#handleArrowKeys);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogCloseEvent.eventName, this.#restoreFocus);
    this.removeEventListener('keydown', this.#handleArrowKeys);
  }

  /** @returns {HTMLButtonElement[]} */
  get #items() {
    return this.refs.items ?? [];
  }

  /**
   * Opens the lightbox on the image that was clicked.
   *
   * @param {PointerEvent} event - The click event.
   */
  open(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const trigger = target.closest('.gallery-item');
    if (!(trigger instanceof HTMLElement)) return;

    const index = this.#items.indexOf(/** @type {HTMLButtonElement} */ (trigger));
    if (index === -1) return;

    this.#lastTrigger = trigger;
    this.#show(index);
    this.showDialog();
  }

  /** Advances to the next image. */
  next() {
    this.#show(this.#index + 1);
  }

  /** Returns to the previous image. */
  previous() {
    this.#show(this.#index - 1);
  }

  /**
   * Renders the image at `index` into the dialog.
   *
   * The set does not wrap: at either end the corresponding control is disabled,
   * so a control never looks live while doing nothing.
   *
   * @param {number} index - Index of the image to show.
   */
  #show(index) {
    const items = this.#items;
    if (items.length === 0) return;

    this.#index = Math.min(Math.max(index, 0), items.length - 1);

    const source = items[this.#index]?.querySelector('img');
    const { lightboxImage, previousButton, nextButton } = this.refs;

    if (source instanceof HTMLImageElement) {
      // Reuse the grid image's own srcset rather than fetching a second
      // high-resolution copy — the item block already emits widths up to 3840.
      lightboxImage.src = source.src;
      lightboxImage.srcset = source.srcset;
      lightboxImage.sizes = '92vw';
      lightboxImage.alt = source.alt;
      lightboxImage.width = source.width;
      lightboxImage.height = source.height;
      this.refs.dialog.setAttribute('aria-label', source.alt);
    }

    previousButton?.setAttribute('aria-disabled', String(this.#index === 0));
    nextButton?.setAttribute('aria-disabled', String(this.#index === items.length - 1));
  }

  /**
   * Moves through the set with the arrow keys while the dialog is open.
   *
   * @param {KeyboardEvent} event - The keydown event.
   */
  #handleArrowKeys = (event) => {
    if (!this.refs.dialog.open) return;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    }
  };

  /** Returns focus to the image that opened the lightbox. */
  #restoreFocus = () => {
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
  };
}

if (!customElements.get('transformation-gallery')) {
  customElements.define('transformation-gallery', TransformationGallery);
}
